import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { projects, categories } from "../../mocks/portfolio";

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const filtered = activeCategory === "All" ? projects : projects.filter((p) => p.category === activeCategory);
  const featured = projects.filter((p) => p.featured);

  return (
    <div className="bg-[#080808] min-h-screen font-['Space_Grotesk']">
      <Navbar />

      {/* ── PAGE HERO ── */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b6fa1577d1d2b096832182cc03cf9500.jpg"
            alt="AWWAL Portfolio"
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/80 to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00ff88]/30 rounded-sm bg-[#00ff88]/5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
            <span className="text-[#00ff88] text-xs tracking-[0.3em] uppercase font-semibold">Our Work</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-none mb-6 max-w-2xl">
            Built to<br /><span className="text-[#00ff88]">Last.</span>
          </h1>
          <p className="text-white/45 text-lg max-w-xl leading-relaxed">
            From mobile RPGs to enterprise-grade social platforms — every project in our portfolio is a testament to what AWWAL does best.
          </p>
        </div>
      </section>

      {/* ── FEATURED ── */}
      <section className="py-20 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="mb-12">
          <div className="text-[#f59e0b] text-xs tracking-[0.3em] uppercase font-semibold mb-4">Spotlight</div>
          <h2 className="text-4xl font-black text-white tracking-tight">Featured Projects</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {featured.map((p, i) => (
            <div
              key={p.id}
              className={`group relative overflow-hidden rounded-sm border border-white/5 hover:border-[#00ff88]/25 transition-all cursor-pointer ${
                i === 0 ? "lg:col-span-2 lg:row-span-2" : ""
              }`}
            >
              <div className={`w-full overflow-hidden ${i === 0 ? "h-80 lg:h-[480px]" : "h-56"}`}>
                <img
                  src={p.image}
                  alt={p.title}
                  title={p.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase rounded-sm bg-[#00ff88]/15 text-[#00ff88] border border-[#00ff88]/25">
                    {p.category}
                  </span>
                  <span className="px-2.5 py-1 text-[10px] font-semibold tracking-widest uppercase rounded-sm bg-white/5 text-white/40 border border-white/10">
                    {p.year}
                  </span>
                </div>
                <h3 className="text-white font-bold text-xl mb-1">{p.title}</h3>
                <p className="text-white/45 text-sm line-clamp-2 leading-relaxed mb-3">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.results.slice(0, 2).map((r) => (
                    <span key={r} className="text-[10px] text-[#f59e0b] font-semibold">{r}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ALL PROJECTS ── */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <div>
              <div className="text-[#00ff88] text-xs tracking-[0.3em] uppercase font-semibold mb-4">All Work</div>
              <h2 className="text-4xl font-black text-white tracking-tight">Full Project Archive</h2>
            </div>
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm transition-all cursor-pointer ${
                    activeCategory === cat
                      ? "bg-[#00ff88] text-[#080808]"
                      : "border border-white/10 text-white/40 hover:text-white hover:border-white/20"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="group bg-[#0f0f0f] border border-white/5 rounded-sm overflow-hidden hover:border-[#00ff88]/20 transition-all cursor-pointer"
              >
                <div className="w-full h-52 overflow-hidden relative">
                  <img
                    src={p.image}
                    alt={p.title}
                    title={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 flex gap-1.5">
                    {p.featured && (
                      <span className="px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase rounded-sm bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[#00ff88] text-[10px] font-bold tracking-widest uppercase">{p.category}</span>
                    <span className="text-white/20 text-[10px]">·</span>
                    <span className="text-white/35 text-[10px] tracking-widest">{p.year}</span>
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{p.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-4 line-clamp-2">{p.desc}</p>
                  {/* Results */}
                  <div className="flex flex-col gap-1.5 mb-4">
                    {p.results.map((r) => (
                      <div key={r} className="flex items-center gap-2 text-xs text-white/50">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-[#00ff88] text-xs leading-none" />
                        </div>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-1.5 pt-4 border-t border-white/5">
                    {p.tech.map((t) => (
                      <span key={t} className="px-2 py-0.5 text-[10px] font-semibold text-white/30 border border-white/8 rounded-sm">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
